// SERVICE D'INTERCEPTION API AVEC REFRESH TOKEN AUTOMATIQUE - VERSION OPTIMISÉE
import store from "../redux/store";
import { refreshAuthToken, logoutUser } from "../redux/actions/LoginActions";
import { DcOffLineHost } from "../config";
import { jwtDecode } from "jwt-decode";

// Variable pour éviter les appels multiples de refresh simultanés
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Vérifie si le token est expiré ou expire bientôt (dans 1 minute)
 * @param {string} token - JWT token
 * @returns {boolean} true si le token doit être rafraîchi
 */
const shouldRefreshToken = (token) => {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const bufferTime = 60; // 1 minute de marge seulement
    const timeUntilExpiry = decoded.exp - currentTime;

    console.log(
      `🔍 Vérification token: expire dans ${Math.round(
        timeUntilExpiry / 60
      )} minutes`
    );

    const shouldRefresh = decoded.exp < currentTime + bufferTime;
    if (shouldRefresh) {
      console.log(
        "⚠️ Token doit être rafraîchi (expire dans moins d'1 minute)"
      );
    } else {
      console.log("✅ Token valide, pas besoin de refresh");
    }

    return shouldRefresh;
  } catch (error) {
    console.warn("Erreur lors du décodage du token:", error);
    return true; // Si on ne peut pas décoder, on rafraîchit par sécurité
  }
};

/**
 * Intercepteur API qui gère automatiquement le refresh token
 * @param {string} url - URL de l'API
 * @param {object} options - Options de la requête fetch
 * @returns {Promise} Response de l'API
 */
export const apiCall = async (url, options = {}) => {
  const dispatch = store.dispatch;

  // Récupération du token
  let token = localStorage.getItem("token");

  // Configuration des headers par défaut
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Ajout du token d'authentification
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Nettoyer les headers undefined
  Object.keys(headers).forEach((key) => {
    if (headers[key] === undefined) {
      delete headers[key];
    }
  });

  // Configuration complète de la requête
  const config = {
    ...options,
    headers,
  };

  try {
    // Première tentative d'appel API
    let response = await fetch(`${DcOffLineHost}${url}`, config);

    // Si token expiré (401), on tente le refresh
    if (response.status === 401 && token) {
      // ✅ Gestion des appels simultanés : éviter les refresh multiples
      if (isRefreshing) {
        // Mettre en queue les requêtes en attente
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            return fetch(`${DcOffLineHost}${url}`, config);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // console.log("🔄 Token expiré, tentative de refresh...");
      isRefreshing = true;

      try {
        // Refresh du token
        const newToken = await dispatch(refreshAuthToken());
        // console.log("✅ Token rafraîchi avec succès");

        // Traiter la queue des requêtes en attente
        processQueue(null, newToken);

        // Mise à jour des headers avec le nouveau token
        config.headers.Authorization = `Bearer ${newToken}`;
        console.log(
          `🔄 Retry avec nouveau token: ${newToken?.substring(0, 50)}...`
        );

        // Nouvelle tentative avec le token rafraîchi
        response = await fetch(`${DcOffLineHost}${url}`, config);
        console.log(`🎯 Retry response status: ${response.status}`);
      } catch (refreshError) {
        console.error("Échec du refresh token:", refreshError);

        // Traiter la queue avec l'erreur
        processQueue(refreshError, null);

        // Déconnexion forcée si le refresh échoue
        await dispatch(logoutUser());

        // Redirection vers la page de connexion
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }

        throw new Error("Session expirée, veuillez vous reconnecter");
      } finally {
        isRefreshing = false;
      }
    }

    return response;
  } catch (error) {
    console.error("Erreur API:", error);
    throw error;
  }
};

/**
 * Wrapper pour les appels GET
 */
export const apiGet = (url, options = {}) => {
  return apiCall(url, { method: "GET", ...options });
};

/**
 * Wrapper pour les appels POST
 */
export const apiPost = (url, data, options = {}) => {
  return apiCall(url, {
    method: "POST",
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Wrapper pour les appels PUT
 */
export const apiPut = (url, data, options = {}) => {
  return apiCall(url, {
    method: "PUT",
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Wrapper pour les appels DELETE
 */
export const apiDelete = (url, options = {}) => {
  return apiCall(url, { method: "DELETE", ...options });
};

/**
 * Wrapper pour les uploads de fichiers
 */
export const apiUpload = (url, formData, options = {}) => {
  const uploadOptions = {
    method: "POST",
    body: formData,
    ...options,
  };

  // Ne pas définir Content-Type pour FormData (le navigateur le fait automatiquement)
  delete uploadOptions.headers?.["Content-Type"];

  return apiCall(url, uploadOptions);
};

export default apiCall;
